(() => {
  if (window.cartAnalyticsInstalled || !window.fetch) return;
  window.cartAnalyticsInstalled = true;

  const script = document.currentScript;
  const nativeFetch = window.fetch;
  const nativeOpen = XMLHttpRequest.prototype.open;
  const xhrListeners = new WeakMap();
  let pendingSync = Promise.resolve();

  /**
   * Recognize storefront cart writes, including locale-prefixed URLs.
   *
   * @param {string|URL} input Request URL.
   * @param {string} method HTTP method.
   * @returns {boolean} Whether this request changes the storefront cart.
   */
  function isCartMutation(input, method) {
    try {
      const url = new URL(input, document.baseURI);
      return method.toUpperCase() === 'POST' && url.origin === window.location.origin && /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(url.pathname);
    } catch (error) {
      return false;
    }
  }

  /**
   * Apply the supplied Google Analytics cookie extraction.
   *
   * @returns {Object} Cart attributes with non-empty cookie values.
   */
  function getAttributes() {
    const c = (p) =>
      document.cookie
        .split('; ')
        .find((r) => r.startsWith(p))
        ?.split('=')[1] || '';
    const attributes = {};
    const clientId = c('_ga=').slice(6);
    const sessionId = c('_ga_').split('$')[0].slice(7);
    if (clientId) attributes._client_id = clientId;
    if (sessionId) attributes._session_id = sessionId;
    return attributes;
  }

  /** Queue cart and line item analytics writes without blocking the original cart request. */
  function syncAttributes() {
    // Serialize our writes, and bypass the wrappers to prevent an update loop.
    pendingSync = pendingSync
      .then(async () => {
        const attributes = getAttributes();
        if (!Object.keys(attributes).length) return;
        const root = window.Shopify?.routes?.root || script?.dataset.root || '/';
        const cartRoot = root.replace(/\/$/, '');
        const response = await nativeFetch.call(window, `${cartRoot}/cart/update.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes }),
          keepalive: true,
        });
        if (!response.ok || !attributes._client_id) return;

        let cart = await response.json();
        const maxUpdates = cart.items.length;
        for (let index = 0; index < maxUpdates; index += 1) {
          const item = cart.items.find((line) => line.properties?._analytics_test !== attributes._client_id);
          if (!item) return;
          const change = await nativeFetch.call(window, `${cartRoot}/cart/change.js`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: item.key,
              quantity: item.quantity,
              properties: { ...item.properties, _analytics_test: attributes._client_id },
            }),
            keepalive: true,
          });
          if (!change.ok) return;
          // Property changes can change line keys or merge lines; use the latest cart.
          cart = await change.json();
        }
      })
      .catch(() => {
        // Analytics failures must not interrupt shopping. Retry on the next change.
      });
  }

  // Observe requests rather than UI events, including requests made by apps.
  window.fetch = function cartAnalyticsFetch(...args) {
    const [input, options] = args;
    const mutation = isCartMutation(input?.url || input, options?.method || input?.method || 'GET');
    return nativeFetch.apply(this, args).then((response) => {
      if (mutation && response.ok) syncAttributes();
      return response;
    });
  };

  // jQuery.ajax and apps may use XMLHttpRequest instead of fetch.
  XMLHttpRequest.prototype.open = function cartAnalyticsOpen(...args) {
    const [method, url] = args;
    const previousListener = xhrListeners.get(this);
    if (previousListener) this.removeEventListener('loadend', previousListener);
    const result = nativeOpen.apply(this, args);

    if (isCartMutation(url, method)) {
      const xhr = this;
      const onLoadEnd = () => {
        xhrListeners.delete(xhr);
        if (xhr.status >= 200 && xhr.status < 300) syncAttributes();
      };
      xhrListeners.set(xhr, onLoadEnd);
      xhr.addEventListener('loadend', onLoadEnd, { once: true });
    } else {
      xhrListeners.delete(this);
    }
    return result;
  };

  // A normal form POST navigates to a new page rather than using fetch/XHR.
  document.addEventListener('DOMContentLoaded', syncAttributes);
})();
